/**
 * Shared marketing copy: the positioning from the Nucleus Systems
 * differentiation brief (Oct 2026), the NS-CTAF v1.0 framework summary,
 * the competitor rows and the sourced market facts. The landing page,
 * /why-paxley, /code-trust and the brochure all read from here so the
 * same claim is never worded two ways.
 *
 * Claim rules (checked against the product, keep them):
 *   - AI remediation is a suggested fix per finding, not an auto-opened PR.
 *   - "Code stays in your environment" only for the GitHub Action and the
 *     self-hosted packages; the hosted GitHub App clones to scan.
 *   - Paxley produces evidence that supports CRA / NIST SSDF / EO 14028;
 *     it does not certify or "satisfy" them.
 *   - SSO, cryptographic attestation, IAST and runtime controls are not
 *     claimed as available.
 *   - No customer or partner names.
 *   - No em or en dashes in visible copy (marketing design rule).
 */

export type Source = { label: string; url: string };

export const SOURCES = {
  dbir: {
    label: "Verizon 2026 Data Breach Investigations Report",
    url: "https://www.verizon.com/about/news/breach-industry-wide-dbir-finds",
  },
  sonatype: {
    label: "Sonatype 2026 State of the Software Supply Chain",
    url: "https://www.sonatype.com/press-releases/sonatype-research-reveals-open-malware-grows-75-percent",
  },
  cra: {
    label: "European Commission, CRA reporting obligations",
    url: "https://digital-strategy.ec.europa.eu/en/policies/cra-reporting",
  },
} satisfies Record<string, Source>;

export const WHY_NOW: Array<{ figure: string; title: string; body: string; source: Source }> = [
  {
    figure: "31%",
    title: "of breaches now start with an exploited vulnerability",
    body: "Exploitation overtook phishing and stolen credentials as the top way in, and 48% of breaches involved a third party.",
    source: SOURCES.dbir,
  },
  {
    figure: "1.23M",
    title: "malicious open-source packages logged",
    body: "Open-source malware grew 75% in a year, while downloads across the major registries passed 9.8 trillion.",
    source: SOURCES.sonatype,
  },
  {
    figure: "24 hours",
    title: "to warn the EU of an exploited vulnerability",
    body: "Cyber Resilience Act reporting has applied to manufacturers since 11 September 2026. You need to know what is in your code before the clock starts.",
    source: SOURCES.cra,
  },
];

export type Differentiator = { n: string; title: string; body: string };

/** The twelve points of the differentiation brief, in its order. */
export const DIFFERENTIATORS: Differentiator[] = [
  {
    n: "01",
    title: "Built at the right time",
    body: "Designed with the lessons of traditional AppSec and today's obligations in view, including the EU Cyber Resilience Act: continuous security, traceability and assurance you can demonstrate.",
  },
  {
    n: "02",
    title: "Supply chain first",
    body: "Connects source code, open-source dependencies, secrets, infrastructure, containers, licences and SBOMs, so risk is visible across the whole delivery chain, not only inside application code.",
  },
  {
    n: "03",
    title: "Code trust, backed by evidence",
    body: "Paxley is the technology and evidence layer for the Nucleus Systems Code Trust Assurance Framework (NS-CTAF): findings become evidence against defined controls.",
  },
  {
    n: "04",
    title: "Zero Trust and code control",
    body: "Built to minimise source-code exposure. Scan inside your own infrastructure with the GitHub Action or a self-hosted package, so sensitive IP stays where it is.",
  },
  {
    n: "05",
    title: "Consolidation that lowers friction",
    body: "SAST, SCA, secrets, IaC, containers, licences, SBOM and code quality in one platform: fewer overlapping tools, integrations, procurement cycles and invoices.",
  },
  {
    n: "06",
    title: "Proven engines, better outcomes",
    body: "Twelve established engines, including Opengrep, CodeQL, OSV-Scanner, Gitleaks and Trivy, with our own work focused on usability, unified reporting, AI-assisted remediation and M&A risk insight.",
  },
  {
    n: "07",
    title: "Up to 90% lower cost",
    body: "Broad AppSec coverage for a fraction of a comparable per-seat tool stack. Fifty developers on ten repositories costs $499 a month on Growth.",
  },
  {
    n: "08",
    title: "No per-seat friction",
    body: "Priced around the products and repositories you protect, so every developer, contractor and auditor can use it without the bill growing with headcount.",
  },
  {
    n: "09",
    title: "Less tool sprawl",
    body: "One platform and one evidence trail instead of several dashboards that disagree, each with its own contract, integration and renewal.",
  },
  {
    n: "10",
    title: "Developer to board",
    body: "The same evidence becomes a fix for the developer, a priority for the security lead, and a clear risk view for executives, boards and acquirers.",
  },
  {
    n: "11",
    title: "Assurance, not just scanning",
    body: "Technical findings are tied to Code Trust controls, turning scanner output into measurable evidence of software integrity and supply-chain security.",
  },
  {
    n: "12",
    title: "Practitioner-built",
    body: "Built from real AppSec, DevSecOps, supply-chain and M&A technology assurance work, which keeps it grounded in business risk as well as engineering.",
  },
];

/** The six the landing page leads with (02, 03, 04, 05, 08, 12). */
export const LEAD_DIFFERENTIATORS = ["02", "03", "04", "05", "08", "12"].map(
  (n) => DIFFERENTIATORS.find((d) => d.n === n)!
);

export const FLOW = [
  "Established engines",
  "Unified risk intelligence",
  "AI-assisted remediation",
  "Trust evidence",
  "Continuous assurance",
];

export type Competitor = { name: string; knownFor: string; paxley: string };

/**
 * Each vendor's best-known focus and where Paxley differs. Statements about
 * the competitor stay at "known for"; the comparison is about Paxley.
 */
export const COMPETITORS: Competitor[] = [
  {
    name: "Snyk",
    knownFor: "Developer-first dependency scanning, priced per developer",
    paxley: "Dependency scanning with transitive resolution and fix versions, plus SAST, IaC, secrets and SBOM, priced per estate, never per seat",
  },
  {
    name: "SonarQube",
    knownFor: "Code quality with security rules",
    paxley: "Security and supply chain first, with code quality alongside and assurance reporting on top",
  },
  {
    name: "Semgrep",
    knownFor: "Fast SAST with a custom rule engine",
    paxley: "SAST plus dependencies, secrets, IaC, containers, licences and SBOM in one package and one bill",
  },
  {
    name: "GitHub Advanced Security",
    knownFor: "Code scanning and secret scanning inside GitHub",
    paxley: "Works on any GitHub plan and on code outside GitHub, and adds IaC, container, licence and SBOM analysis",
  },
  {
    name: "Checkmarx",
    knownFor: "Enterprise SAST and application security posture",
    paxley: "Built for the mid-market: installs in minutes, transparent pricing from $199 a month",
  },
  {
    name: "GitGuardian",
    knownFor: "Secrets detection",
    paxley: "Secrets detection plus the rest of the code security stack in the same report",
  },
  {
    name: "Black Duck",
    knownFor: "Open-source audits and SBOMs for M&A",
    paxley: "A code risk assessment for M&A from $1,500, with SAST, secrets and runtime support status in the same report",
  },
];

// ──────────────────────────────────────────────────────────────────
// NS-CTAF v1.0
// ──────────────────────────────────────────────────────────────────

export type Coverage = "strong" | "partial" | "assessed";

export const COVERAGE_LABEL: Record<Coverage, string> = {
  strong: "Evidence from Paxley today",
  partial: "Partial evidence from Paxley",
  assessed: "Assessed by Nucleus Systems",
};

export type Domain = {
  id: string;
  name: string;
  weight: number;
  controls: string[];
  coverage: Coverage;
  /** What the platform contributes to this domain, in plain words. */
  paxley: string;
};

export const DOMAINS: Domain[] = [
  {
    id: "D1",
    name: "Identity and provenance",
    weight: 18,
    coverage: "partial",
    paxley: "Automated SBOM generation on every scan, component origin from the package registries, and commit provenance for the scanned revision.",
    controls: [
      "Developer identity verification",
      "Cryptographic commit signing",
      "Contribution attribution and audit trail",
      "Signing key lifecycle management",
      "Build identity attestations",
      "Pipeline identity and credentials",
      "Automated SBOM generation",
      "SBOM lifecycle management",
      "Component origin verification",
      "Contributor trust weighting",
      "External contributor vetting",
      "Credential revocation",
      "Trust lineage graph",
      "AI-generated code attribution",
      "Federated identity standards",
    ],
  },
  {
    id: "D2",
    name: "Integrity and immutability",
    weight: 18,
    coverage: "assessed",
    paxley: "Reviewed in a Code Trust assessment. Signing, provenance and attestation evidence is on the platform roadmap.",
    controls: [
      "Signed commits enforcement",
      "Artifact signing",
      "Build provenance attestation",
      "Tamper-evident pipeline",
      "Reproducible builds",
      "Immutable artifact storage",
      "Pipeline integrity monitoring",
      "Dependency hash verification",
      "Release integrity gates",
      "Container image signing",
      "Package hash verification",
      "Integrity exception governance",
      "in-toto framework adoption",
      "SLSA level achievement",
      "Binary transparency logging",
    ],
  },
  {
    id: "D3",
    name: "Secure development",
    weight: 22,
    coverage: "strong",
    paxley: "SAST as a pull-request gate, IaC and container scanning, secrets detection, and rules for LLM and agent code. Only new findings block a change.",
    controls: [
      "Threat modelling",
      "Security requirements definition",
      "Secure coding standards",
      "Role-specific security training",
      "SAST as a CI/CD gate",
      "DAST integration",
      "IAST and RASP deployment",
      "Fuzz testing programme",
      "Security peer review",
      "Pre-commit security hooks",
      "Security Champions programme",
      "Security requirements traceability",
      "AI-generated code review policy",
      "IaC security scanning",
      "Container vulnerability scanning",
      "API security testing",
      "Security regression testing",
      "Penetration testing",
    ],
  },
  {
    id: "D4",
    name: "Dependency and supply chain",
    weight: 20,
    coverage: "strong",
    paxley: "Full dependency inventory including transitive packages, CVE monitoring with EPSS and CISA KEV, licence policy, end-of-life runtimes and SBOM to CVE correlation.",
    controls: [
      "Dependency inventory",
      "Automated CVE monitoring",
      "Dependency risk scoring",
      "Patch SLA enforcement",
      "Licence compliance",
      "Transitive dependency analysis",
      "Vendor and OSS assessment",
      "Supply chain attack detection",
      "Automated dependency updates",
      "End-of-life tracking",
      "Private registry control",
      "Dependency pinning",
      "SBOM to CVE correlation",
      "Policy as code enforcement",
      "Dependency vetting workflow",
      "Contractual SBOM obligations",
    ],
  },
  {
    id: "D5",
    name: "Runtime behaviour",
    weight: 14,
    coverage: "assessed",
    paxley: "Runtime controls sit outside source code. They are reviewed in a Code Trust assessment alongside the platform evidence.",
    controls: [
      "Runtime anomaly detection",
      "Behavioural baseline",
      "Drift detection",
      "RASP protection",
      "Application behavioural monitoring",
      "Kernel-level restrictions",
      "Audit logging and distributed tracing",
      "Runtime exploit detection",
      "Memory safety controls",
      "Process behaviour analytics",
      "Runtime trust score updates",
      "Cloud-native runtime controls",
    ],
  },
  {
    id: "D6",
    name: "Governance and accountability",
    weight: 8,
    coverage: "partial",
    paxley: "Compliance reporting mapped to real evidence, a scan and suppression audit trail with reasons and expiry, and licence policy as code.",
    controls: [
      "Code Trust governance charter",
      "Executive accountability",
      "RACI matrix",
      "Audit trail management",
      "Compliance reporting",
      "Policy as code",
      "Certification programme management",
      "Third-party trust obligations",
      "Regulatory compliance matrix",
      "Continuous improvement programme",
    ],
  },
];

export const MATURITY = [
  { level: "L1", name: "Initial", body: "Ad hoc" },
  { level: "L2", name: "Developing", body: "Practised, not yet standard" },
  { level: "L3", name: "Defined", body: "Documented and consistent" },
  { level: "L4", name: "Managed", body: "Measured" },
  { level: "L5", name: "Optimised", body: "Continuously improved" },
];

export const EVIDENCE_TIERS = [
  { tier: "T1", name: "Policy and documentation" },
  { tier: "T2", name: "Tooling configuration" },
  { tier: "T3", name: "Automated pipeline output" },
  { tier: "T4", name: "Independent attestation" },
];

export const CTA_LEVELS = [
  {
    id: "CTA-1",
    name: "Transparent",
    body: "L2 in identity, supply chain and governance. 45+ controls, SBOM generation, basic identity and governance controls.",
  },
  {
    id: "CTA-2",
    name: "Verified",
    body: "L3 across identity, integrity and secure development. 63+ controls, artifact signing, SLSA Build L2, a SAST gate and threat modelling.",
  },
  {
    id: "CTA-3",
    name: "Assured",
    body: "L3.5+ in every domain. 78+ controls, SLSA Build L3, CVE SLAs and runtime anomaly detection.",
  },
  {
    id: "CTA-4",
    name: "Adaptive trust",
    body: "L4+ in every domain. All 86 controls, SLSA Build L4, reproducible builds, a Trust API and continuous re-certification.",
  },
];

export const STANDARDS = ["SLSA", "NIST SSDF", "OWASP SAMM", "EU CRA", "EO 14028"];

/** Counted from DOMAINS so the headline number cannot drift from the list. */
export const CONTROL_COUNT = DOMAINS.reduce((n, d) => n + d.controls.length, 0);

export const CREDENTIALS = [
  { k: "13 years", v: "Nucleus Systems, a global cybersecurity advisory firm" },
  { k: "350+", v: "M&A technology diligence engagements" },
  { k: "26+ years", v: "in application security, led by CEO Godfrey Kutumela (CISSP, CCSP, CSSLP, ISO 27001 LA)" },
  { k: "2 years", v: "refined as a managed service before launch" },
];
